import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import Alert from '@material-ui/core/Alert';
import CircularProgress from '@material-ui/core/CircularProgress';
import { registerCourseUser } from '../../../../services/userService';
import { sendEmail } from '../../../../services/emailService';

const COURSE_NAME = 'Oratoria Estratégica';
const ADMIN_EMAIL = 'enteratecon.ve@gmail.com';
const FROM_EMAIL = 'noreply@tnonetwork.live';
const REPLY_TO_EMAIL = 'tnoacademy@gmail.com';

const RegistroCursoForm = () => {
  const { pathname } = useLocation();
  const courseSlug = (pathname || '')
    .replace(/^\/+|\/+$/g, '')
    .split('/')[0] || 'oratoria';

  const [form, setForm] = useState({
    name: '',
    lastName: '',
    email: '',
    phone: '',
    notes: '',
  });
  const [status, setStatus] = useState({ type: null, message: '' });
  const [loading, setLoading] = useState(false);

  const getSourceUrl = () => {
    try {
      if (typeof window === 'undefined') return '';
      return window.location.href || '';
    } catch (_) {
      return '';
    }
  };

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    if (status.type) setStatus({ type: null, message: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus({ type: null, message: '' });
    const submitted = { ...form };
    const result = await registerCourseUser({ ...submitted, courseSlug, courseName: COURSE_NAME });
    setLoading(false);
    if (result.success) {
      const fullName = `${(submitted.name || '').trim()} ${(submitted.lastName || '').trim()}`.trim();
      const sourceUrl = getSourceUrl();
      const ts = new Date().toISOString();

      // Emails informativos (no bloquean el registro).
      Promise.allSettled([
        sendEmail({
          to: [ADMIN_EMAIL],
          from: FROM_EMAIL,
          replyTo: REPLY_TO_EMAIL,
          subject: `Nuevo registro: ${COURSE_NAME} (${courseSlug})`,
          text:
            `Se registró una persona para ${COURSE_NAME}.\n\n` +
            `Nombre: ${fullName}\n` +
            `Email: ${(submitted.email || '').trim()}\n` +
            `Teléfono: ${(submitted.phone || '').trim() || '—'}\n` +
            `Comentarios: ${(submitted.notes || '').trim() || '—'}\n` +
            `Curso: ${COURSE_NAME}\n` +
            `Slug: ${courseSlug}\n` +
            `URL: ${sourceUrl || '—'}\n` +
            `Fecha: ${ts}\n`,
        }),
        sendEmail({
          to: [(submitted.email || '').trim()],
          from: FROM_EMAIL,
          replyTo: REPLY_TO_EMAIL,
          subject: '🎙️ ¡Bienvenido(a) a Oratoria Estratégica! Tu transformación comienza aquí.',
          text:
            `# 🎙️ ¡Bienvenido(a) a Oratoria Estratégica! Tu transformación comienza aquí.\n\n` +
            `Hola **${fullName || '[Nombre del Participante]'}**, \n\n` +
            `Es un placer para nosotros darte la bienvenida oficial a la mentoría **Oratoria Estratégica**.\n\n` +
            `Has tomado la decisión de invertir en tu activo más valioso: **tu voz**. A partir de ahora, dejas de ser un espectador para convertirte en el protagonista de tu propio mensaje, y lo harás en el mejor escenario posible: el ecosistema digital de **TNO Network**.\n\n` +
            `Estamos listos para acompañarte en este viaje de cuatro sesiones donde, de la mano de **Flavia Sánchez**, descubrirás que el miedo escénico es solo el impulso para tu próximo gran discurso.\n\n` +
            `---\n\n` +
            `## 📍 Coordenadas de tu experiencia\n\n` +
            `- **Inicio:** Sábado 09 de mayo de 2026.\n` +
            `- **Horario:** 2:00 pm a 5:00 pm.\n` +
            `- **Lugar:** Sede TNO Network, Las Mercedes, Caracas. [Insertar link de Google Maps aquí].\n` +
            `- **Instructora:** Flavia Sánchez (@enterateconflavia).\n\n` +
            `## 📝 Información importante para tu primer día\n\n` +
            `- **Puntualidad:** Te recomendamos llegar 15 minutos antes para el registro y para que te vayas familiarizando con el ambiente de estudio.\n` +
            `- **Preparación:** Ven con ropa cómoda y, sobre todo, con la mente abierta. Vamos a entrar en cabina, usaremos micrófonos y cámaras desde la primera sesión.\n` +
            `- **Materiales:** Nosotros te entregaremos tu guía de ejercicios y material de apoyo estratégico, pero siempre es bueno traer tu libreta favorita para apuntes personales.\n` +
            `- **Networking:** Prepárate para conocer a otros profesionales y emprendedores. El Coffee Break será el momento ideal para crear alianzas.\n\n` +
            `## ✨ Tu kit de bienvenida incluye\n\n` +
            `- Acceso exclusivo a las instalaciones de TNO Network.\n` +
            `- Material de apoyo digital y físico.\n` +
            `- Certificado de asistencia (al finalizar las 4 sesiones).\n\n` +
            `Si tienes alguna duda antes de nuestro primer encuentro, puedes escribirnos directamente a este correo o vía WhatsApp al **0422-2020113**.\n\n` +
            `Estamos emocionados por ver todo lo que tienes para decir al mundo. ¡Nos vemos en el estudio!\n\n` +
            `---\n\n` +
            `Atentamente,\n\n` +
            `**El Equipo de TNO Network & Flavia Sánchez**\n` +
            `*The Content Evolution.*`,
          html:
            `<div style="font-family:Arial,Helvetica,sans-serif;line-height:1.5">` +
            `<h2 style="margin:0 0 10px">🎙️ ¡Bienvenido(a) a Oratoria Estratégica! Tu transformación comienza aquí.</h2>` +
            `<p style="margin:0 0 12px">Hola <strong>${fullName || '[Nombre del Participante]'}</strong>,</p>` +
            `<p style="margin:0 0 12px">Es un placer para nosotros darte la bienvenida oficial a la mentoría <strong>Oratoria Estratégica</strong>.</p>` +
            `<p style="margin:0 0 12px">Has tomado la decisión de invertir en tu activo más valioso: tu voz. A partir de ahora, dejas de ser un espectador para convertirte en el protagonista de tu propio mensaje, y lo harás en el mejor escenario posible: el ecosistema digital de <strong>TNO Network</strong>.</p>` +
            `<p style="margin:0 0 12px">Estamos listos para acompañarte en este viaje de cuatro sesiones donde, de la mano de Flavia Sánchez, descubrirás que el miedo escénico es solo el impulso para tu próximo gran discurso.</p>` +
            `<h3 style="margin:18px 0 8px">📍 Coordenadas de tu experiencia</h3>` +
            `<p style="margin:0 0 8px"><strong>Inicio:</strong> Sábado 09 de mayo de 2026.</p>` +
            `<p style="margin:0 0 8px"><strong>Horario:</strong> 2:00 pm a 5:00 pm.</p>` +
            `<p style="margin:0 0 8px"><strong>Lugar:</strong> Sede TNO Network, Las Mercedes, Caracas. [Insertar link de Google Maps aquí].</p>` +
            `<p style="margin:0 0 12px"><strong>Instructora:</strong> Flavia Sánchez (@enterateconflavia).</p>` +
            `<h3 style="margin:18px 0 8px">📝 Información importante para tu primer día</h3>` +
            `<p style="margin:0 0 8px"><strong>Puntualidad:</strong> Te recomendamos llegar 15 minutos antes para el registro y para que te vayas familiarizando con el ambiente de estudio.</p>` +
            `<p style="margin:0 0 8px"><strong>Preparación:</strong> Ven con ropa cómoda y, sobre todo, con la mente abierta. Vamos a entrar en cabina, usaremos micrófonos y cámaras desde la primera sesión.</p>` +
            `<p style="margin:0 0 8px"><strong>Materiales:</strong> Nosotros te entregaremos tu guía de ejercicios y material de apoyo estratégico, pero siempre es bueno traer tu libreta favorita para apuntes personales.</p>` +
            `<p style="margin:0 0 12px"><strong>Networking:</strong> Prepárate para conocer a otros profesionales y emprendedores. El Coffee Break será el momento ideal para crear alianzas.</p>` +
            `<h3 style="margin:18px 0 8px">✨ Tu kit de bienvenida incluye</h3>` +
            `<ul style="margin:0 0 12px 20px;padding:0">` +
            `<li style="margin:0 0 6px">Acceso exclusivo a las instalaciones de TNO Network.</li>` +
            `<li style="margin:0 0 6px">Material de apoyo digital y físico.</li>` +
            `<li style="margin:0 0 6px">Certificado de asistencia (al finalizar las 4 sesiones).</li>` +
            `</ul>` +
            `<p style="margin:0 0 12px">Si tienes alguna duda antes de nuestro primer encuentro, puedes escribirnos directamente a este correo o vía WhatsApp al <strong>0422-2020113</strong>.</p>` +
            `<p style="margin:0 0 12px">Estamos emocionados por ver todo lo que tienes para decir al mundo. ¡Nos vemos en el estudio!</p>` +
            `<p style="margin:16px 0 0;color:#666">Atentamente,<br/>El Equipo de TNO Network & Flavia Sánchez<br/>The Content Evolution.</p>` +
            `</div>`,
        }),
      ]).catch(() => {});

      setStatus({
        type: 'success',
        message: '¡Registro recibido! Te contactaremos por WhatsApp o correo para confirmar tu cupo y próximos pasos.',
      });
      setForm({ name: '', lastName: '', email: '', phone: '', notes: '' });
    } else {
      setStatus({ type: 'error', message: result.error || 'Error al registrar. Intenta de nuevo.' });
    }
  };

  return (
    <Box>
      <Box marginBottom={3}>
        <Box
          component={Typography}
          fontWeight={900}
          variant="h4"
          gutterBottom
          align="center"
        >
          Regístrate y asegura tu cupo
        </Box>
        <Typography align="center" color="textSecondary" variant="body1" sx={{ maxWidth: 760, mx: 'auto' }}>
          Completa el formulario y te contactaremos para confirmar tu inscripción en <strong>{COURSE_NAME}</strong>.
        </Typography>
      </Box>

      <Box maxWidth={520} margin="0 auto">
        <Box component="form" noValidate autoComplete="off" onSubmit={handleSubmit}>
          {status.type === 'success' && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setStatus({ type: null, message: '' })}>
              {status.message}
            </Alert>
          )}
          {status.type === 'error' && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setStatus({ type: null, message: '' })}>
              {status.message}
            </Alert>
          )}

          <Box display="flex" flexDirection="column" gap={2}>
            <TextField
              required
              label="Nombre"
              variant="outlined"
              color="primary"
              size="medium"
              fullWidth
              value={form.name}
              onChange={handleChange('name')}
            />
            <TextField
              required
              label="Apellido"
              variant="outlined"
              color="primary"
              size="medium"
              fullWidth
              value={form.lastName}
              onChange={handleChange('lastName')}
            />
            <TextField
              required
              label="Correo"
              type="email"
              variant="outlined"
              color="primary"
              size="medium"
              fullWidth
              value={form.email}
              onChange={handleChange('email')}
            />
            <TextField
              label="Teléfono / WhatsApp (opcional)"
              variant="outlined"
              color="primary"
              size="medium"
              fullWidth
              value={form.phone}
              onChange={handleChange('phone')}
            />
            <TextField
              label="Comentarios (opcional)"
              variant="outlined"
              color="primary"
              size="medium"
              fullWidth
              multiline
              rows={2}
              value={form.notes}
              onChange={handleChange('notes')}
              placeholder="Ej. ¿Vienes a potenciar presentaciones, cámaras o reuniones?"
            />

            <Button
              sx={{ height: 54 }}
              type="submit"
              variant="contained"
              color="primary"
              size="large"
              fullWidth
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {loading ? 'Enviando…' : 'Enviar registro'}
            </Button>
          </Box>

          <Typography variant="caption" color="textSecondary" display="block" align="center" sx={{ mt: 2 }}>
            Al enviar, autorizas que te contactemos para información del curso. (Curso: {courseSlug})
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default RegistroCursoForm;

